import { useTranslation } from 'react-i18next';

// Current UI language, 'bs' unless the visitor switched to English.
export const useLang = () => {
  const { i18n } = useTranslation();
  return i18n.language === 'en' ? 'en' : 'bs';
};

// Picks the Bs/En variant of a settings field, falling back to a default pair.
// pick(settings, 'heroTitle', lang, ['Bosanski', 'English']) reads heroTitleBs/heroTitleEn.
export const pick = (settings, key, lang, [fallbackBs, fallbackEn] = ['', '']) => {
  if (lang === 'en') return settings?.[`${key}En`] || fallbackEn || settings?.[`${key}Bs`] || fallbackBs;
  return settings?.[`${key}Bs`] || fallbackBs;
};

// Package fields are either a plain string or a { bs, en } object.
export const localized = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.bs || value.en || '';
};

// Same rule the old sections used: full URLs and absolute paths stay as they
// are, bare filenames live in /img/photos/.
export const normalizeSrc = (src) => {
  if (!src) return '';
  const s = String(src).trim();
  if (s.startsWith('http') || s.startsWith('/') || s.startsWith('data:')) return s;
  return `/img/photos/${s}`;
};

export const isVideoFile = (url) => /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || '');

export const getYouTubeEmbed = (url) => {
  try {
    if (url.includes('youtube.com/shorts/')) return `https://www.youtube.com/embed/${url.split('shorts/')[1].split(/[?&]/)[0]}`;
    if (url.includes('youtu.be/')) return `https://www.youtube.com/embed/${url.split('youtu.be/')[1].split(/[?&]/)[0]}?rel=0`;
    const m = url.match(/[?&]v=([^&]+)/);
    if (m && m[1]) return `https://www.youtube.com/embed/${m[1]}?rel=0`;
  } catch (e) { /* not a YouTube URL */ }
  return null;
};

// Strips formatting so the number works in tel: and wa.me links.
export const sanitizePhone = (raw) => {
  if (!raw) return '';
  const digits = String(raw).trim().replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? digits : `+${digits.replace(/^\+/, '')}`;
};

// Contact fields have been saved under more than one name over time.
export const contactInfo = (settings) => {
  const phoneRaw = settings?.contactPhone || settings?.phone || '+387 61 680 413';
  const email = settings?.contactEmail || settings?.email || 'selimovicerminphoto@gmail.com';
  const phone = sanitizePhone(phoneRaw);
  return {
    phoneRaw,
    phone,
    email,
    whatsapp: `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent('Pozdrav, zanima me ponuda.')}`,
    instagram: settings?.instagramUrl || settings?.instagram || '',
    facebook: settings?.facebook || '',
  };
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// The two hero diamonds: { src, x, y, zoom } each. Uses settings.heroDiamonds
// (set in the admin editor) and falls back to the first hero/gallery images.
const DIAMOND_DEFAULT = { src: '', x: 50, y: 50, zoom: 1 };
export const heroDiamondsFrom = (settings) => {
  if (Array.isArray(settings?.heroDiamonds) && settings.heroDiamonds.some((d) => d?.src)) {
    return [0, 1].map((i) => ({ ...DIAMOND_DEFAULT, ...(settings.heroDiamonds[i] || {}) }));
  }
  const pool = [...(settings?.heroImages || []), ...(settings?.galleryImages || [])].filter(Boolean);
  return [
    { ...DIAMOND_DEFAULT, src: pool[0] || '/img/gallery/2.webp' },
    { ...DIAMOND_DEFAULT, src: pool[1] || '/img/gallery/13.webp' },
  ];
};

// Inline style that frames a photo inside a diamond: zoom grows the photo
// around its centre, x/y pick the focal point.
export const diamondImageStyle = (d) => {
  const pct = 150 * (d.zoom || 1);
  return {
    width: `${pct}%`, height: `${pct}%`, left: `${(100 - pct) / 2}%`, top: `${(100 - pct) / 2}%`,
    objectPosition: `${d.x ?? 50}% ${d.y ?? 50}%`,
  };
};
